import { useState } from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Link } from 'expo-router';
import { colors, control, fontFamily, price, radii, shadows, space, spacing, type } from '~/ui/tokens';
import { computeDiscountAmount, formatCurrency, formatMoney } from '../lib/money';
import { cityCountryOf } from '../lib/location';
import { useCurrency } from '~/stores/useCurrency';
import { Tooltip } from './ui/Tooltip';
import type { HotelSummary } from '../lib/api/types';

type SelectRateCardProps = {
  hotel: HotelSummary;
  cardWidth: number;
  compact: boolean;
  onPress?: () => void;
  href?: string;
};

const SELECT_RATE_CARD_IMAGE_ASPECT_RATIO = 312 / 164;

function reviewLabel(score: number) {
  if (score >= 9) return 'Outstanding';
  if (score >= 8.5) return 'Excellent';
  if (score >= 8) return 'Very good';
  if (score >= 7) return 'Good';
  return 'Guest rating';
}

// Homepage-only vertical price card. Reserved identity and policy regions keep
// pricing aligned without per-hotel exceptions or a fixed overall card height.
export function SelectRateCard({ hotel, cardWidth, compact, onPress, href }: SelectRateCardProps) {
  const selectedCurrency = useCurrency((state) => state.selectedCurrency);
  const [hovered, setHovered] = useState(false);
  const saving = computeDiscountAmount(hotel.lowestPrice, hotel.retailPrice, selectedCurrency);
  const featuredRate = hotel.rates.reduce(
    (lowest, rate) => (Number(rate.price.amount) < Number(lowest.price.amount) ? rate : lowest),
    hotel.rates[0],
  );
  const hasRatePolicy = Boolean(featuredRate);
  const hasBreakfast = featuredRate?.meal.hasBreakfast === true;
  const hasFreeCancellation = Boolean(featuredRate?.cancellation.freeCancellationBefore);
  const reviewScore = hotel.reviewScore;

  const cardContent = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`View ${hotel.name}`}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={styles.cardPressable}
    >
        {/* Zone 1 — image */}
        <View style={styles.imageWrap}>
          {hotel.thumbnail ? (
            <Image source={hotel.thumbnail} style={styles.image} resizeMode="cover" />
          ) : (
            <View style={[styles.image, styles.imageEmpty]}>
              <Ionicons name="image-outline" size={28} color={colors.inkFaint} />
            </View>
          )}
          {typeof reviewScore === 'number' ? (
            <View style={styles.reviewBadge}>
              <Text style={styles.reviewScore}>{reviewScore.toFixed(1)}</Text>
              <Text style={styles.reviewDescription}>{reviewLabel(reviewScore)}</Text>
            </View>
          ) : null}
          <View style={styles.locationBadge}>
            <Ionicons name="location-outline" size={13} color={colors.white} />
            <Text style={styles.locationText} numberOfLines={1}>{cityCountryOf(hotel.address)}</Text>
          </View>
        </View>

        <View style={styles.content}>
          {/* Zone 2 — title/info */}
          <View style={styles.identity}>
            <Text style={styles.name} numberOfLines={2}>
              {hotel.name}
            </Text>
          </View>

          {/* Zone 3 — stars and a reserved two-line inclusion region. */}
          <View style={styles.details}>
            <View style={styles.starsRow}>
              {Array.from({ length: Math.min(5, hotel.starRating) }).map((_, index) => (
                <Ionicons key={index} name="star" size={14} color={colors.brand} />
              ))}
            </View>

            {/* Zone 3 — eligible policies share one compact metadata row. */}
            {hasRatePolicy ? <View style={[styles.inclusions, compact && styles.inclusionsCompact]}>
              <View style={[styles.inclusionItem, compact && styles.inclusionItemCompact]}>
                <Ionicons name={hasFreeCancellation ? 'checkmark-circle-outline' : 'close-circle-outline'} size={14} color={hasFreeCancellation ? colors.green : colors.inkFaint} />
                <Text style={[styles.inclusionText, compact && styles.inclusionTextCompact]}>{hasFreeCancellation ? 'Free cancellation' : 'Non-refundable'}</Text>
              </View>
              <View style={[styles.inclusionItem, compact && styles.inclusionItemCompact]}>
                <Ionicons name="cafe-outline" size={14} color={hasBreakfast ? colors.green : colors.inkFaint} />
                <Text style={[styles.inclusionText, compact && styles.inclusionTextCompact]}>{hasBreakfast ? 'Breakfast included' : 'Breakfast not included'}</Text>
              </View>
            </View> : (
                <View style={styles.inclusionItem}>
                  <Ionicons name="information-circle-outline" size={14} color={colors.inkFaint} />
                  <Text style={styles.inclusionText}>Rate policy unavailable</Text>
                </View>
            )}
          </View>

          {/* Zone 4 — one balanced divider, followed by a two-column pricing
              footer whose CTA aligns with the taxes line. */}
          <View style={styles.priceArea}>
            <View style={styles.pricingLayout}>
              <View style={styles.pricingCopy}>
                <Text style={styles.onlineLabel}>Online price</Text>
                <View style={styles.priceTopRow}>
                  <Text style={styles.onlinePrice}>{formatMoney(hotel.retailPrice)}</Text>
                  {saving > 0 ? (
                    <View style={styles.savingsPill}>
                      <Text style={styles.savingsText}>
                        Save {formatCurrency(saving, selectedCurrency)}
                      </Text>
                    </View>
                  ) : null}
                </View>
                <Tooltip nested label="Free to unlock — just sign in with Google. No coupons, no subscription.">
                  <View style={styles.memberLabelRow}>
                    <Text style={styles.memberLabel}>SuiteScanner+ price</Text>
                    <Ionicons name="information-circle-outline" size={12} color={colors.inkFaint} />
                  </View>
                </Tooltip>
                <View style={styles.mainPriceRow}>
                  <Text style={styles.mainPrice}>{formatMoney(hotel.lowestPrice)}</Text>
                  <Text style={styles.perNight}>/night</Text>
                </View>
                <Text style={styles.taxesText}>Online taxes &amp; fees included</Text>
              </View>

              <View style={styles.ctaRow}>
                <Text style={styles.viewHotelText}>View hotel</Text>
                <Ionicons name="arrow-forward" size={14} color={colors.brand} />
              </View>
            </View>
          </View>
        </View>
      </Pressable>
    );

    return (
      <View
        style={[
          styles.wrap,
          { width: cardWidth },
          compact && styles.wrapCompact,
          hovered && styles.wrapHovered,
        ]}
      >
        {href ? (
          <Link href={href as any} asChild>
            {cardContent}
          </Link>
        ) : (
          cardContent
        )}
      </View>
    );
  }

const styles = StyleSheet.create({
  wrap: {
    height: 458,
    flexShrink: 0,
    backgroundColor: colors.card,
    borderRadius: radii.md,
    // No overflow:hidden here — that would clip this same view's own
    // box-shadow to almost nothing. The image's corners are clipped one
    // level down instead, on cardPressable, which carries no shadow of
    // its own to lose.
    ...shadows.sm,
  },
  wrapCompact: {
    height: 438,
  },
  wrapHovered: {
    transform: [{ translateY: -2 }],
    ...shadows.card,
  },
  cardPressable: {
    flex: 1,
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  imageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: SELECT_RATE_CARD_IMAGE_ASPECT_RATIO,
    backgroundColor: colors.lineSoft,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  reviewBadge: {
    position: 'absolute',
    top: spacing(1.25),
    left: spacing(1.25),
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: colors.brand,
  },
  reviewScore: {
    fontFamily: fontFamily.semibold,
    fontSize: 14,
    color: colors.white,
    fontVariant: ['tabular-nums'],
  },
  reviewDescription: {
    fontFamily: fontFamily.semibold,
    fontSize: 12,
    color: colors.white,
  },
  locationBadge: {
    position: 'absolute',
    left: spacing(1.25),
    bottom: spacing(1.25),
    maxWidth: '78%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
    borderRadius: radii.full,
    backgroundColor: 'rgba(15, 23, 42, 0.78)',
    paddingVertical: spacing(0.625),
    paddingHorizontal: spacing(1),
  },
  locationText: {
    flexShrink: 1,
    fontFamily: fontFamily.medium,
    ...type.metadata,
    color: colors.white,
  },
  content: {
    flex: 1,
    padding: space.internal,
  },
  identity: {
    gap: space.micro,
  },
  name: {
    height: type.cardTitle.lineHeight * 2,
    flexShrink: 0,
    fontFamily: fontFamily.bold,
    ...type.cardTitle,
    color: colors.ink,
  },
  details: {
    marginTop: space.related,
    minHeight: 50,
  },
  starsRow: {
    minHeight: type.metadata.lineHeight,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  inclusions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginTop: 10,
    gap: 12,
  },
  inclusionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
    flexShrink: 0,
  },
  inclusionText: {
    fontFamily: fontFamily.medium,
    fontSize: type.metadata.fontSize,
    color: colors.inkSoft,
  },
  inclusionsCompact: { gap: 6 },
  inclusionItemCompact: { gap: 3, flexShrink: 1 },
  inclusionTextCompact: { fontSize: 10, lineHeight: 14, flexShrink: 1 },
  priceArea: {
    marginTop: 'auto',
    borderTopWidth: 1,
    borderTopColor: colors.lineSoft,
    paddingTop: 12,
  },
  pricingLayout: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  pricingCopy: {
    flex: 1,
    minWidth: 0,
  },
  onlineLabel: {
    marginBottom: 2,
    fontFamily: fontFamily.regular,
    fontSize: 11,
    lineHeight: 14,
    color: colors.inkFaint,
  },
  priceTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    minHeight: price.publicCompare.lineHeight,
  },
  onlinePrice: {
    fontFamily: fontFamily.medium,
    ...price.publicCompare,
    color: colors.inkFaint,
    textDecorationLine: 'line-through',
    fontVariant: ['tabular-nums'],
  },
  savingsPill: {
    flexShrink: 1,
    backgroundColor: colors.greenTint,
    borderRadius: radii.full,
    paddingVertical: spacing(0.5),
    paddingHorizontal: spacing(1),
  },
  savingsText: {
    fontFamily: fontFamily.semibold,
    ...price.savingsPill,
    color: colors.green,
  },
  memberLabelRow: {
    marginTop: space.micro,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
  },
  memberLabel: {
    fontFamily: fontFamily.medium,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  mainPriceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: space.micro,
    marginTop: space.micro,
  },
  mainPrice: {
    fontFamily: fontFamily.bold,
    ...price.featured,
    color: colors.ink,
    fontVariant: ['tabular-nums'],
  },
  perNight: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  taxesText: {
    marginTop: space.micro,
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
    paddingBottom: 1,
    flexShrink: 0,
  },
  viewHotelText: {
    fontFamily: fontFamily.semibold,
    fontSize: control.cardCta.fontSize,
    color: colors.brand,
  },
});
